import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { EncFilterField, Encoding } from '@mode-vegas/vegas';
import { ButtonComponent, IconComponent } from '@mode/capra';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledField } from '@mode/shared/util-switch';
import { isEqual } from 'lodash';
import { RelativeDatePanelComponent } from '../../relative-date-panel';
import * as util from '../filter-modal-helpers';
import { FilterStep } from '../filter-modal.component';
import { FilterRangeNumericComponent } from './filter-range/filter-range-numeric.component';
import { FilterValuesComponent } from './filter-values/filter-values.component';

enum FilterConfigurationType {
  RangeRelativeDate,
  RangeNumeric,
  IndividualValues,
}

/**
 * This is the main wrapper component which holds the filter-type
 * specific configuration panels. In addition to displaying the
 * correct panel, this component hosts the main dialog action
 * buttons (reset, cancel, apply, etc) and maintains the dirty/invalid
 * filter state.
 *
 * Each of the child configuration components is responsible for
 * emitting events when the filter changes or when the filter is
 * invalid.
 *
 * This component holds those filter changes. If the user applies
 * the changes, the new filter is passed up to the main modal for
 * saving. If the user resets, the last applied filter is reset
 * back on the child component.
 *
 * Each of the child configuration components expects to receive
 * a valid filter of the correct type. Since the initial filter
 * may be undefined, this component generates an empty filter
 * if necessary to set on the child component.
 */
@Component({
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    IconComponent,
    FilterValuesComponent,
    FilterRangeNumericComponent,
    RelativeDatePanelComponent,
  ],
  selector: 'mode-filter-configuration',
  templateUrl: './filter-configuration.component.html',
  styleUrls: ['./filter-configuration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterConfigurationComponent implements OnChanges {
  @Input() field!: CompiledField;
  @Input() initialFilter: EncFilterField | undefined;
  @Input() view!: FlamingoTypes.ModeView;
  @Input() encoding!: Encoding;
  @Input() queryRunToken!: string;
  @Input() step!: FilterStep;

  @Output() canceled = new EventEmitter<void>();
  @Output() fieldChanged = new EventEmitter<CompiledField>();
  @Output() applyFilter = new EventEmitter<{ filter: EncFilterField; close: boolean }>();

  FilterConfigurationTypeEnum = FilterConfigurationType;

  filterConfigType: FilterConfigurationType = FilterConfigurationType.IndividualValues;

  currentFilter: EncFilterField | undefined;
  lastAppliedFilter: EncFilterField | undefined;

  // For most filters, the step does not change once the user has selected
  // the type. However, for 'individual values' the user can toggle between
  // a list (MultiValues) or TopN (TopByField).
  // When a switch is made, we need to pass the correct 'current' filter
  // to the configuration panel.
  // The following two maps keep the 'current' and 'last applied' by the FilterStep
  // sothat the users config isn't lost switching between the types.
  private currentFilterByStepMap = new Map<FilterStep, EncFilterField | undefined>();
  private lastAppliedByStepMap = new Map<FilterStep, EncFilterField | undefined>();

  hasChanges = false;
  isInvalid = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.initialFilter !== undefined) {
      this.updateCurrentFilterState();
    }
    if (this.step === FilterStep.Range) {
      this.filterConfigType = FilterConfigurationType.RangeNumeric;
    } else if (this.step === FilterStep.RelativeDateRange) {
      this.filterConfigType = FilterConfigurationType.RangeRelativeDate;
    } else if (this.step === FilterStep.MultiValues || this.step === FilterStep.TopByField) {
      this.filterConfigType = FilterConfigurationType.IndividualValues;
    } else {
      throw Error(
        `Unable to determine filter configuration type for step: ${this.step} and filter type: ${this.currentFilter?.filterType}`
      );
    }
  }

  onFilterChange(updatedFilter: EncFilterField) {
    const fieldDidChange = this.checkFieldDidChange(updatedFilter);

    this.currentFilterByStepMap.set(this.step, updatedFilter);
    this.currentFilter = updatedFilter;

    // Check if the filter has actually changed. Include the default
    // value for optional settings so that a new filter with the
    // default set doesn't trigger Apply or Reset.
    this.hasChanges = !isEqual(
      { showRelevantValuesOnly: false, ...this.currentFilter },
      { showRelevantValuesOnly: false, ...this.lastAppliedFilter }
    );

    // If the formula changed then we have a new formula to display in the header
    if (fieldDidChange) {
      this.fieldChanged.emit(this.field.withNewFormula(this.currentFilter.formula));
    }
  }

  onFilterReplace(updatedFilter: EncFilterField) {
    this.lastAppliedByStepMap.set(this.step, updatedFilter);
    this.lastAppliedFilter = updatedFilter;
    this.onFilterChange(updatedFilter);
  }

  onStepChange(step: FilterStep) {
    this.step = step;
    this.updateCurrentFilterState();
  }

  onIsInvalidChange(isInvalid: boolean) {
    this.isInvalid = isInvalid;
  }

  /**
   * Updates the current and last applied filter to match the current
   * FilterStep. It may create a new empty filter if there is no
   * current filter and either no initial filter or the initial filter
   * doesn't match the current step.
   */
  updateCurrentFilterState() {
    let currentFilter = this.currentFilterByStepMap.get(this.step);
    if (currentFilter === undefined) {
      const allowedTypes = util.getFilterTypesForStep(this.step);
      if (this.initialFilter === undefined || !allowedTypes.includes(this.initialFilter.filterType)) {
        currentFilter = util.makeEmptyFilter(this.field, this.view, this.encoding, this.step);
      } else {
        currentFilter = this.initialFilter;
      }
      this.currentFilterByStepMap.set(this.step, currentFilter);
    }
    let lastApplied = this.lastAppliedByStepMap.get(this.step);
    if (lastApplied === undefined) {
      lastApplied = currentFilter;
      this.lastAppliedByStepMap.set(this.step, lastApplied);
    }

    this.currentFilter = currentFilter;
    this.lastAppliedFilter = lastApplied;

    if (this.currentFilter && this.checkFieldDidChange()) {
      this.fieldChanged.emit(this.field.withNewFormula(this.currentFilter.formula));
    }
  }

  doCancel() {
    this.canceled.emit();
  }

  doReset() {
    const fieldDidChange = this.checkFieldDidChange();

    // Set the current filter back to the last applied filter. This will
    // get passed down to the child components
    this.currentFilter = this.lastAppliedFilter;
    this.currentFilterByStepMap.set(this.step, this.currentFilter);
    this.hasChanges = false;

    // If the formula changed then we have a new formula to display in the header
    if (this.currentFilter && fieldDidChange) {
      this.fieldChanged.emit(this.field.withNewFormula(this.currentFilter.formula));
    }
  }

  doApply(close: boolean) {
    if (this.currentFilter !== undefined) {
      this.lastAppliedByStepMap.set(this.step, this.currentFilter);
      this.lastAppliedFilter = this.currentFilter;
      this.hasChanges = false;
      this.applyFilter.emit({ filter: this.currentFilter, close });
    }
  }

  /**
   * Compares the current filter's formula to another filter's formula, returning true
   * if they differ. If "other" is not provided the default is the last applied filter
   */
  private checkFieldDidChange(other = this.lastAppliedFilter): boolean {
    return this.currentFilter?.formula !== other?.formula;
  }
}
